'use client'

import React, { useEffect, useState, useSyncExternalStore } from "react"
import { createPortal } from "react-dom"
import { useSession } from "next-auth/react"
import { toast } from "@/lib/toast"
import { useRouter, usePathname } from "next/navigation"
import { navigateUrl } from "@/utils/client-request"
import { usePaymentChecker, useInAppBrowser, IN_APP_BROWSER_LABELS, type PaymentEndpoint } from "@/lib/commerce/hooks"
import { FREEFORM_ADDRESS_CHANNELS, STATUS_LABELS, TERMINAL_STATUSES, PAYMENT_METHOD_LABELS, DIGITAL_DOWNLOAD_STATUSES, formatStepDate, formatAddressType, formatOrderAddress, getCustomerName, getVariantDimension, isCustomFieldVisible, getProductPricing, calcPriceBreakdown, buildDigitalDownloadUrl, downloadDigitalFile, bookloDisplayStatus, showsReadyStep, calcDepositBreakdown, getPayableAmount, hasLiveInvoice, resolveOrderStatus, DEPOSIT_LABELS } from "@/utils/order"
import DepositBalanceCard from "@/components/blocks/DepositBalanceCard"
import QRCodeImport from "react-qr-code"
const QRCode = QRCodeImport as unknown as React.FC<{ value: string, size?: number, fgColor?: string, bgColor?: string, style?: React.CSSProperties }>
import { bankList, formatDateLong, formatDateNormal, formatDateTimeLong, formatImageURL, formatNumber, formatPrice, isCustomerAuthActive } from "@/utils/util"
import { uploadURL } from "@/lib/customFields"
import { ArrowRight, ExternalLink } from "lucide-react"
import Image from "next/image"
import qpayLogo from "@/assets/qpay.svg"
import pocketzeroLogo from "@/assets/pocketzero.svg"
import storepayLogo from "@/assets/storepay.png"
import lendmnLogo from "@/assets/lendmn.png"

const focusRing = 'focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-foreground'
const eyebrow = 'text-[11px] tracking-[0.14em] uppercase text-muted-foreground'
const solidButton = `inline-flex items-center justify-center gap-2 h-12 px-8 bg-foreground text-background text-[13px] tracking-wide transition-opacity duration-150 hover:opacity-85 disabled:opacity-50 disabled:cursor-not-allowed ${focusRing}`
const outlinedButton = `inline-flex items-center justify-center gap-2 h-9 px-4 border border-foreground text-foreground text-xs tracking-wide transition-colors duration-150 hover:bg-foreground hover:text-background disabled:opacity-50 ${focusRing}`
const sweepLink = `pb-0.5 bg-[linear-gradient(currentColor,currentColor)] bg-no-repeat bg-position-[0_100%] bg-size-[0%_1px] hover:bg-size-[100%_1px] transition-[background-size] duration-500 ease-out motion-reduce:transition-none ${focusRing}`
const QR_INK = '#09090b'

// Buyer-supplied, so anything uploadURL cannot place in our bucket is dropped.
function customFieldImages(value: any): string[] {
  return (Array.isArray(value) ? value : [value]).map(image => uploadURL(image)).filter((url): url is string => !!url)
}

function renderCustomFieldValue(field: any, dateFormatter: (v: any) => string) {
  if (field.type === 'checkbox') return field.value === 'true' || field.value === true ? 'Тийм' : 'Үгүй'
  if (field.type === 'date') return dateFormatter(field.value)
  if (field.type === 'file') {
    const url = uploadURL(field.value)
    return url && <a href={url} target="_blank" rel="noopener noreferrer" className="text-foreground underline underline-offset-4">Файл харах</a>
  }
  if (field.type === 'productImage') return (
    <span className="inline-flex flex-wrap gap-1 align-middle">
      {customFieldImages(field.value).map((image: string) => (
        <a key={image} href={image} target="_blank" rel="noopener noreferrer">
          <img src={image} alt="" className="w-10 aspect-3/4 object-cover bg-muted" />
        </a>
      ))}
    </span>
  )
  return field.value
}

function StatusPill({ status, order }: { status: string, order?: any }) {
  const shown = resolveOrderStatus(order, status)
  const label = STATUS_LABELS[shown] || 'Тодорхойгүй'
  const isAlert = shown === 'canceled' || shown === 'deliveryFailed'
  return (
    <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.14em] uppercase text-foreground">
      <span className={`relative inline-flex size-1.5 rounded-full ${isAlert ? 'bg-red-600 text-red-600' : 'bg-foreground text-foreground'}`}>
        {shown === 'pending' && <span className="ping-ring" />}
      </span>
      {label}
    </span>
  )
}

function InfoRow({ label, children }: { icon?: any, label: string, children: any }) {
  return (
    <div className="py-3 grid grid-cols-[110px_minmax(0,1fr)] gap-4 items-baseline">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-[13px] wrap-break-word">{children}</dd>
    </div>
  )
}

function CopyField({ label, value, mono }: { label: string, value: string, mono?: boolean }) {
  const [flashed, setFlashed] = useState(false)
  const handle = () => {
    if (!value) return toast.error('Хуулах утга байхгүй байна')
    navigator.clipboard?.writeText(value)
    setFlashed(true)
    setTimeout(() => setFlashed(false), 900)
  }
  return (
    <div>
      <div className={`mb-2 ${eyebrow}`}>{label}</div>
      <button type="button" onClick={handle} className={`group w-full min-h-11 flex items-center gap-3 border px-3 py-2.5 text-left transition-colors duration-150 ${flashed ? 'border-foreground bg-muted' : 'border-border hover:border-foreground'} ${focusRing}`}>
        <span className={'flex-1 text-sm text-foreground ' + (mono ? 'font-mono tabular-nums break-all' : '')}>{value}</span>
        <span className="text-xs text-muted-foreground shrink-0 transition-colors duration-150 group-hover:text-foreground">{flashed ? 'Хуулсан' : 'Хуулах'}</span>
      </button>
    </div>
  )
}

function OrderTitle({ order, status }: { order: any, status: string }) {
  return (
    <header>
      <div className={eyebrow}>Захиалга</div>
      <h1 className="mt-3 text-4xl sm:text-5xl font-light tracking-tight tabular-nums text-foreground">#{order?.customId}</h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
        <StatusPill status={status} order={order} />
        <span className="text-[13px] text-muted-foreground tabular-nums">
          {formatDateTimeLong(order?.createdAt)}
          {order?.products?.length ? ` · ${order.products.length} бараа` : ''}
          {getCustomerName(order) ? ` · ${getCustomerName(order)}` : ''}
        </span>
      </div>
    </header>
  )
}

function OrderStepper({ order, vendor, status }: { order: any, vendor: any, status: string, primary?: string }) {
  const isPickup = order?.type === 'pickup'
  const isRefundLike = status === 'refunded' || status === 'partiallyRefunded'

  if (TERMINAL_STATUSES.has(status) || isRefundLike) {
    const stamp = status === 'canceled' ? order?.canceledAt
      : isRefundLike ? order?.refundedAt
        : status === 'deliveryFailed' ? order?.deliveryFailedAt : null
    const reason = status === 'canceled' ? order?.canceledReason
      : isRefundLike ? order?.refundedReason
        : status === 'deliveryFailed' ? order?.deliveryFailedReason : null
    const subText = status === 'canceled' ? 'Захиалга цуцлагдсан'
      : isRefundLike ? 'Буцаалт хийгдсэн'
        : 'Хүргэлт амжилтгүй болсон'
    return (
      <div className="border-l border-foreground pl-4">
        <div className="text-sm text-foreground">
          {STATUS_LABELS[status]}
          {reason ? <span className="ml-1.5 text-muted-foreground">({reason})</span> : null}
        </div>
        <div className="mt-1 text-xs text-muted-foreground tabular-nums">
          {subText}
          {stamp ? ` · ${formatDateTimeLong(stamp)}` : ''}
        </div>
      </div>
    )
  }

  // Pay-after-delivery reverses the last two steps: the goods arrive, then the
  // money. Payment gets its own trailing step keyed off paidAt, since order.status
  // never passes through 'paid' for these.
  const isOnDelivery = order?.payAfterDelivery
  const baseSteps = isOnDelivery ? [
    { key: 'pending', label: 'Захиалсан', at: order?.createdAt },
    { key: 'ready', label: 'Бэлэн', at: order?.readyAt },
    { key: 'shipped', label: 'Хүргэлтэнд', at: order?.shippedAt },
    { key: 'delivered', label: 'Хүргэгдсэн', at: order?.deliveredAt },
    { key: 'settled', label: 'Төлсөн', at: order?.paidAt },
  ] : [
    { key: 'pending', label: 'Захиалсан', at: order?.createdAt },
    { key: 'paid', label: 'Төлсөн', at: order?.paidAt },
    { key: 'ready', label: 'Бэлэн', at: order?.readyAt },
    { key: 'shipped', label: 'Хүргэлтэнд', at: order?.shippedAt },
    { key: 'delivered', label: 'Хүргэгдсэн', at: order?.deliveredAt },
  ]
  const hasReadyStep = showsReadyStep(order, vendor)
  /*
    Split payment gets its own step. Without it, "deposit paid, waiting on the goods"
    looks identical to "nothing has happened yet" — and that is the state a pre-order
    sits in for its entire lead time, so it is the one the customer checks on most.
  */
  const isDeposit = !!(order?.depositAmount > 0)
  const withDeposit = isDeposit
    ? [baseSteps[0], { key: 'deposit', label: 'Урьдчилгаа', at: order?.depositPaidAt }, ...baseSteps.slice(1)]
    : baseSteps
  const steps = withDeposit.filter(s => !(isPickup && s.key === 'shipped') && !(!hasReadyStep && s.key === 'ready'))
  // `status` is the Booklo-aware displayStatus, so the stepper advances correctly.
  const targetKey = status === 'partiallyRefunded' ? 'delivered' : status
  let cur = Math.max(0, steps.findIndex(s => s.key === targetKey))
  // The deposit landing is real progress, but the order is still 'pending' — nothing in
  // the status ladder would move the marker off step 0 on its own.
  if (isDeposit && order?.depositPaidAt && cur < 1) cur = 1
  /*
    Payment sits last in this layout because a pay-after-delivery order settles at the
    door — so paidAt implied everything before it was done. A split payment breaks that:
    the customer can clear the balance days before the goods move, and jumping the marker
    to the end painted "Хүргэлтэнд" and "Хүргэгдсэн" as complete when neither had
    happened. Only advance when the goods actually went out; the settled step still ticks
    on its own via the `at` rule below.
  */
  if (isOnDelivery && order?.paidAt && (status === 'delivered' || order?.deliveredAt)) cur = steps.length - 1
  const prog = (cur / (steps.length - 1)) * 100

  return (
    <div className="relative">
      <div
        aria-hidden
        className="absolute top-[4px] h-px"
        style={{
          left: `${50 / steps.length}%`,
          right: `${50 / steps.length}%`,
          background: `linear-gradient(to right, var(--foreground) ${prog}%, var(--border) ${prog}%)`,
        }}
      />
      <ol className="relative grid" style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
        {steps.map((s, i) => {
          // A step past the marker that carries its own timestamp really did happen —
          // paying the balance early ticks Төлсөн without implying delivery.
          const done = i < cur || (i > cur && !!s.at)
          const active = i === cur
          return (
            <li key={s.key} aria-current={active ? 'step' : undefined} className="flex flex-col items-center text-center">
              <span className={`relative size-[9px] rounded-full ${done ? 'bg-foreground' : active ? 'bg-background ring-1 ring-foreground text-foreground' : 'bg-background ring-1 ring-border'}`}>
                {active && <span className="ping-ring" />}
              </span>
              <span className={`mt-3 text-xs ${done || active ? 'text-foreground' : 'text-muted-foreground'}`}>{s.label}</span>
              <span className="mt-1 text-[10px] text-muted-foreground tabular-nums">{s.at ? formatStepDate(s.at) : '—'}</span>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

function DigitalDownloadRow({ order, vendor }: { order: any, vendor: any, primary?: string }) {
  const [downloading, setDownloading] = useState(false)
  const digitalProducts = (order?.products || []).filter((p: any) => {
    const productData = p.product || p
    return productData?.isDigital && p.variant?.digitalFile
  })
  if (!digitalProducts.length || !DIGITAL_DOWNLOAD_STATUSES.has(order?.status)) return null

  const single = digitalProducts.length === 1
  const firstStatus = single
    ? (order?.digitalDownloadStatus || []).find((s: any) => s?.variantId === digitalProducts[0].variant.id) || null
    : null
  const firstUnavailable = !!firstStatus && (firstStatus.expired || firstStatus.downloadsRemaining === 0)
  const firstUrl = single && !firstUnavailable ? buildDigitalDownloadUrl(order, vendor, digitalProducts[0].variant.id) : null

  return (
    <section className="py-6 border-y border-border flex flex-col sm:flex-row sm:items-center gap-4">
      <div className="flex-1 min-w-0">
        <div className="text-sm text-foreground">Дижитал бүтээгдэхүүн татах</div>
        <div className="mt-1 text-xs text-muted-foreground">
          {single ? 'Доорх товчлуурыг дарж файлаа татаж авна уу.' : `${digitalProducts.length} файл бэлэн боллоо. Барааны жагсаалтаас тус бүрд нь татна уу.`}
        </div>
      </div>
      {single && firstUrl && (
        <div className="flex flex-col sm:items-end gap-2 shrink-0">
          <button type="button" disabled={downloading} onClick={async () => { setDownloading(true); await downloadDigitalFile(firstUrl); setDownloading(false) }} className={outlinedButton}>
            {downloading ? 'Татаж байна…' : 'Файл татах'}
          </button>
          {firstStatus && !firstStatus.expired && (
            <div className="text-[11px] text-muted-foreground tabular-nums">
              Үлдсэн: <span className="text-foreground">{firstStatus.downloadsRemaining}/{firstStatus.downloadsMax}</span>
              {firstStatus.expiresAt && <span> · {formatDateNormal(new Date(firstStatus.expiresAt))} хүртэл</span>}
            </div>
          )}
        </div>
      )}
    </section>
  )
}

function RefundBanner({ order }: { order: any }) {
  const status = order?.status
  if (status !== 'refunded' && status !== 'partiallyRefunded') return null
  const isPartial = status === 'partiallyRefunded'
  const refundedAmount = order?.totalRefunded || (isPartial ? 0 : order?.amount)
  return (
    <div className="border-l border-foreground pl-4">
      <div className="text-sm text-foreground">
        {isPartial ? 'Хэсэгчилсэн буцаалт' : 'Бүрэн буцаалт'}
        {order?.refundedReason ? <span className="ml-1.5 text-muted-foreground">({order.refundedReason})</span> : null}
      </div>
      <div className="mt-1 text-xs leading-relaxed text-muted-foreground">
        Таны {formatPrice(refundedAmount)} нь 3-5 ажлын өдөрт буцаагдах болно. Хэрэв хүлээн аваагүй тохиолдолд дэлгүүртэй холбогдоно уу.
      </div>
    </div>
  )
}

function FooterActions({ vendor, pathname }: { vendor: any, pathname: string }) {
  const { data: session } = useSession()
  const customerLoggedIn = !!session?.user?.id
  const showMyOrders = !isCustomerAuthActive(vendor) || customerLoggedIn
  return (
    <div className="pt-6 border-t border-border flex flex-wrap items-center gap-x-8 gap-y-3 text-[13px] print:hidden">
      <button type="button" onClick={() => window.print()} className={sweepLink}>Хэвлэх</button>
      <a href="https://help.shop.mn" target="_blank" rel="noopener noreferrer" className={sweepLink}>Тусламж</a>
      {showMyOrders && (
        <a href={navigateUrl('/order', pathname)} className={`sm:ml-auto inline-flex items-center gap-1.5 ${sweepLink}`}>
          Миний захиалгууд<ArrowRight className="size-3.5" strokeWidth={1.5} />
        </a>
      )}
    </div>
  )
}

const subscribeNoop = () => () => {}

function PrintableReceipt({ order, vendor }: { order: any, vendor: any }) {
  const mounted = useSyncExternalStore(subscribeNoop, () => true, () => false)
  if (!mounted) return null
  return createPortal(<PrintableReceiptBody order={order} vendor={vendor} />, document.body)
}

function PrintableReceiptBody({ order, vendor }: { order: any, vendor: any }) {
  const { salePriceSavings, discountBreakdown } = calcPriceBreakdown(order)
  const totalDue = (order?.amount || 0) - (order?.totalRefunded || 0)
  const isDelivery = order?.type === 'delivery'
  const orderProducts = order?.products || []
  const allDigital = orderProducts.length > 0 && orderProducts.every((p: any) => (p.product || p)?.isDigital === true)
  const showAddressOrBranch = !allDigital
  const customerName = getCustomerName(order) || '-'
  const addressText = isDelivery
    ? formatOrderAddress(order?.address, order?.channel)
    : (order?.branchData || order?.branch)
      ? `${order?.branchData?.name || order?.branch?.name}, ${order?.branchData?.address || order?.branch?.address}`
      : '-'
  return (
    <div data-print-root className="hidden print:block text-black text-[12px] leading-snug">
      <style>{`@page { size: A4; margin: 16mm; }`}</style>
      <div className="text-center mb-4">
        <div className="text-lg font-medium">{vendor?.name || 'Shop.mn'}</div>
        <div className="text-[11px]">Захиалгын баримт</div>
      </div>
      <div className="flex justify-between border-y border-black py-2 mb-3">
        <div>
          <div className="font-medium">Захиалга #{order?.customId}</div>
          <div className="text-[11px]">{formatDateTimeLong(order?.createdAt)}</div>
        </div>
        <div className="text-right">
          <div className="text-[11px]">Төлөв</div>
          <div className="font-medium">{STATUS_LABELS[order?.status] || '-'}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-3">
        <div>
          <div className="text-[10px] uppercase font-medium mb-1">Хүлээн авагч</div>
          <div>{customerName}</div>
          <div>{order?.phone}{order?.additionalPhone ? ` · ${order.additionalPhone}` : ''}</div>
          {order?.email && <div>{order.email}</div>}
        </div>
        {showAddressOrBranch && (
          <div>
            <div className="text-[10px] uppercase font-medium mb-1">{isDelivery ? 'Хүргэх хаяг' : 'Авах салбар'}</div>
            <div>{addressText}</div>
          </div>
        )}
      </div>
      <table className="w-full border-collapse mb-3">
        <thead>
          <tr className="border-y border-black">
            <th className="text-left py-1 font-medium">Бараа</th>
            <th className="text-right py-1 font-medium w-16">Тоо</th>
            <th className="text-right py-1 font-medium w-24">Үнэ</th>
            <th className="text-right py-1 font-medium w-24">Дүн</th>
          </tr>
        </thead>
        <tbody>
          {(order?.products || []).map((p: any, i: number) => {
            const productData = p.product || p
            const productName = productData?.name || p?.name || 'Бүтээгдэхүүн'
            const { unitPrice, lineTotal } = getProductPricing(p)
            const qty = p?.quantity || 0
            return (
              <tr key={i} className="border-b border-border align-top">
                <td className="py-1 pr-2">
                  {productName}
                  {p.variant?.name && <div className="text-[10px]">{p.variant.name}</div>}
                </td>
                <td className="py-1 text-right tabular-nums">{qty}</td>
                <td className="py-1 text-right tabular-nums">{formatPrice(unitPrice)}</td>
                <td className="py-1 text-right tabular-nums">{formatPrice(lineTotal)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <div className="ml-auto w-1/2 text-[11px]">
        <div className="flex justify-between py-0.5"><span>Бараанын дүн</span><span className="tabular-nums">{formatPrice(order?.subAmount)}</span></div>
        {salePriceSavings > 0 && <div className="flex justify-between py-0.5"><span>Хямдрал</span><span className="tabular-nums">-{formatPrice(salePriceSavings)}</span></div>}
        {discountBreakdown.map(d => (
          <div key={d.id} className="flex justify-between py-0.5"><span>Урамшуулал ({d.name})</span><span className="tabular-nums">-{formatPrice(d.amount)}</span></div>
        ))}
        {(order?.couponAmount || 0) > 0 && (
          <div className="flex justify-between py-0.5"><span>Купон{order?.couponData?.name ? ` (${order.couponData.name})` : ''}</span><span className="tabular-nums">-{formatPrice(order?.couponAmount)}</span></div>
        )}
        {(order?.giftCardAmount || 0) > 0 && (
          <div className="flex justify-between py-0.5"><span>Бэлгийн карт</span><span className="tabular-nums">-{formatPrice(order?.giftCardAmount)}</span></div>
        )}
        {(order?.loyaltyDiscountAmount || 0) > 0 && (
          <div className="flex justify-between py-0.5"><span>Лояалти хөнгөлөлт</span><span className="tabular-nums">-{formatPrice(order?.loyaltyDiscountAmount)}</span></div>
        )}
        {(order?.loyaltyVoucherAmount || 0) > 0 && (
          <div className="flex justify-between py-0.5"><span>Лояалти ваучер</span><span className="tabular-nums">-{formatPrice(order?.loyaltyVoucherAmount)}</span></div>
        )}
        {isDelivery && (
          <div className="flex justify-between py-0.5"><span>Хүргэлт</span><span className="tabular-nums">{vendor?.deliveryType === 'laterDelivery' && order?.deliveryPrice === 0 ? 'Нэмэлтээр тооцно' : order?.deliveryPrice === 0 ? 'Үнэгүй' : formatPrice(order?.deliveryPrice)}</span></div>
        )}
        {(order?.totalRefunded || 0) > 0 && (
          <div className="flex justify-between py-0.5"><span>Буцаалт</span><span className="tabular-nums">-{formatPrice(order?.totalRefunded)}</span></div>
        )}
        <div className="flex justify-between border-t border-black mt-1 pt-1 font-medium text-[13px]">
          <span>Нийт төлөх</span>
          <span className="tabular-nums">{formatPrice(totalDue)}</span>
        </div>
      </div>
      <div className="mt-6 text-center text-[10px]">
        Энэхүү баримтыг {formatDateTimeLong(new Date())}-нд хэвлэв · {vendor?.name || 'Shop.mn'}
      </div>
    </div>
  )
}

function PaidConfirmation({ order, status }: { order: any, status: string, primary?: string }) {
  const pathname = usePathname()
  const isDelivered = status === 'delivered'
  const isShipped = status === 'shipped'
  // 'Төлбөр амжилттай' is only true when money has actually arrived — a
  // pay-after-delivery order reaches 'ready' without it.
  const isUnpaidOnDelivery = order?.payAfterDelivery && !order?.paidAt
  // Split payment: the deposit landed but the balance has not.
  const deposit = calcDepositBreakdown(order)
  const label = isDelivered ? 'Захиалга хүргэгдсэн' : isShipped ? 'Хүргэлтэнд гарсан' : deposit.isAwaitingBalance ? 'Урьдчилгаа төлөгдсөн' : isUnpaidOnDelivery ? 'Захиалга баталгаажсан' : 'Төлбөр амжилттай'
  const title = isDelivered ? 'Танд баярлалаа' : isShipped ? 'Захиалга замдаа явж байна' : 'Захиалга баталгаажигдлаа'
  const subtitle = isDelivered ? 'Бараа тань таалагдсан бол үнэлгээ бичиж үлдээгээрэй!'
    : isShipped ? 'Таны захиалга хүргэлтэнд гарсан. Хүргэлтийн явцыг эндээс хянаарай.'
      : 'Бид бараагаа бэлтгэж эхэллээ. Төлөв өөрчлөгдөхөд танд мэдэгдэнэ.'
  const tiles = [
    { label: 'Захиалга', value: `#${order?.customId}` },
    // What was actually handed over, not what the order is worth — on a split
    // payment those differ, and order.amount here would tell a customer who paid
    // 20% that they have paid in full.
    { label: 'Төлсөн дүн', value: formatPrice(deposit.paidSoFar) || '0₮' },
    // While a balance is outstanding it displaces the delivery-date tile: it is
    // the one thing the customer still has to act on.
    deposit.isAwaitingBalance
      ? { label: 'Үлдэгдэл', value: formatPrice(deposit.outstanding) || '0₮' }
      : { label: isDelivered ? 'Хүргэгдсэн' : 'Хүргэгдэх', value: isDelivered ? (order?.deliveredAt ? formatDateTimeLong(order.deliveredAt) : '—') : 'Удахгүй' },
  ]
  return (
    <section className="py-8 border-y border-border">
      <div className={eyebrow}>{label}</div>
      <h2 className="mt-3 text-2xl sm:text-3xl tracking-tight text-foreground">{title}</h2>
      <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>
      <dl className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
        {tiles.map(tile => (
          <div key={tile.label}>
            <dt className={eyebrow}>{tile.label}</dt>
            <dd className="mt-2 text-base tabular-nums text-foreground">{tile.value}</dd>
          </div>
        ))}
      </dl>
      {(order?.loyaltyPointsAwarded || 0) > 0 && (
        <a href={navigateUrl('/points', pathname)} className={`mt-8 inline-flex items-center gap-1.5 text-[13px] ${sweepLink}`}>
          <span className="tabular-nums">+{formatNumber(order.loyaltyPointsAwarded)} оноо</span> энэ захиалгаас цуглууллаа
          <ArrowRight className="size-3.5" strokeWidth={1.5} />
        </a>
      )}
    </section>
  )
}

function Countdown({ countdown }: { countdown: { h: number, m: number, s: number, expired: boolean } }) {
  const pad = (n: number) => String(n).padStart(2, '0')
  const units = [
    { v: countdown.h, lab: 'цаг' },
    { v: countdown.m, lab: 'мин' },
    { v: countdown.s, lab: 'сек' },
  ]
  return (
    <section className="py-6 border-y border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
      <div>
        <div className="text-sm text-foreground">Төлбөр хүлээгдэж байна</div>
        <p className="mt-1 text-xs text-muted-foreground">Төлбөрөө хугацаанд нь төлж, захиалгаа баталгаажуулна уу.</p>
      </div>
      <div className="flex items-start gap-5 tabular-nums" role="timer" aria-label={`${countdown.h} цаг ${countdown.m} минут үлдсэн`}>
        {units.map(u => (
          <div key={u.lab} className="flex flex-col items-center">
            <span aria-hidden className="text-3xl font-light leading-none">{pad(u.v)}</span>
            <span aria-hidden className="mt-2 text-[11px] tracking-[0.14em] uppercase text-muted-foreground">{u.lab}</span>
          </div>
        ))}
      </div>
    </section>
  )
}

function BankTransferCard({ order }: { order: any, primary?: string }) {
  const bank = order?.vendor?.bank
  const bankName = bank?.bankName || '—'
  const bankIcon = bankList.find(b => b.code == bank?.bankCode)?.icon
  const reference = `#${order?.customId}`

  return (
    <section>
      <div className="border border-border">
        <div className="px-5 py-4 flex items-center gap-4 border-b border-border">
          <div className="size-10 overflow-hidden bg-muted grid place-items-center shrink-0">
            {bankIcon && <Image src={bankIcon} width={40} height={40} alt={bankName} className="w-full h-full object-cover" />}
          </div>
          <div className="min-w-0">
            <div className={eyebrow}>Хүлээн авагч банк</div>
            <div className="mt-1 text-base text-foreground truncate">{bankName}</div>
          </div>
        </div>
        <div className="px-5 py-5 grid gap-5">
          <CopyField label="IBAN · Дансны дугаар" value={bank?.accountIban || ''} mono />
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <div className={`mb-2 ${eyebrow}`}>Хүлээн авагч</div>
              <div className="min-h-11 flex items-center border border-border px-3 py-2.5 text-sm text-foreground">{bank?.accountName || '—'}</div>
            </div>
            <CopyField label="Гүйлгээний утга" value={reference} mono />
          </div>
        </div>
        <div className="px-5 py-5 border-t border-border flex items-end justify-between gap-4">
          <div className="min-w-0">
            <div className={eyebrow}>Шилжүүлэх дүн</div>
            <div className="mt-2 text-3xl font-light tabular-nums tracking-tight">{formatPrice(getPayableAmount(order))}</div>
          </div>
          <div className="text-right shrink-0">
            <div className={eyebrow}>Захиалга</div>
            <div className="mt-2 text-base font-mono">#{order?.customId}</div>
          </div>
        </div>
      </div>
      <p className="mt-4 border-l border-foreground pl-4 text-xs leading-relaxed text-muted-foreground">
        Гүйлгээний утга хэсэгт <span className="text-foreground">#{order?.customId}</span> захиалгын дугаарыг заавал бичнэ үү. Төлбөр хүлээн авмагц захиалга баталгаажна.
      </p>
    </section>
  )
}

function ProductItem({ product, vendor, pathname, order }: { product: any, isFirst?: boolean, vendor: any, pathname: string, order: any }) {
  const [downloading, setDownloading] = useState(false)
  const productData = product.product || product
  const productName = productData?.name || product?.name || 'Бүтээгдэхүүн'
  const productCustomId = productData?.customId || product?.customId
  const variant = product.variant
  const isReward = product.isRewardItem
  const discount = !isReward ? product.discount : null
  const hasDiscount = discount && (discount.type === 'productDiscount' || discount.type === 'order')

  const { unitPrice, originalUnitPrice, discountApplied } = getProductPricing(product)
  const dimension = getVariantDimension(variant)
  const visibleCustomFields = (product.customFields || []).filter(isCustomFieldVisible)
  const isFullyRefunded = product?.refunded && product?.quantity === 0
  const isPartiallyRefunded = product?.refunded && product?.quantity > 0
  const downloadStatus = productData?.isDigital && variant?.id
    ? (order?.digitalDownloadStatus || []).find((s: any) => s?.variantId === variant.id) || null
    : null
  const isExpired = !!downloadStatus?.expired
  const isLimitHit = !!downloadStatus && downloadStatus.downloadsRemaining === 0 && !isExpired
  const downloadAllowed = productData?.isDigital
    && variant?.digitalFile
    && DIGITAL_DOWNLOAD_STATUSES.has(order?.status)
    && !isExpired
    && !isLimitHit
  const downloadUrl = downloadAllowed ? buildDigitalDownloadUrl(order, vendor, variant.id) : null
  const productHref = navigateUrl(`/product/${productCustomId}`, pathname)

  const tags = [
    isFullyRefunded ? 'Буцаагдсан' : null,
    isPartiallyRefunded ? `Хэсэгчлэн буцаасан · ${product?.refundedQuantity}ш` : null,
    isReward ? (product.discountName || 'Бэлэг') : null,
    hasDiscount && discountApplied ? discount.name : null,
    productData?.isDigital ? 'Дижитал' : null,
  ].filter(Boolean) as string[]

  return (
    <li className="flex gap-4 sm:gap-5 py-6">
      <a href={productHref} className={`w-20 sm:w-24 shrink-0 ${focusRing}`}>
        <div className="aspect-3/4 bg-muted overflow-hidden">
          <img src={formatImageURL(variant?.images?.[0] || 'placeholder.png')} alt={productName} className={'w-full h-full object-cover ' + (isFullyRefunded ? 'opacity-50 grayscale' : '')} />
        </div>
      </a>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <a href={productHref} className={`block text-sm leading-snug line-clamp-2 ${isFullyRefunded ? 'text-muted-foreground line-through' : 'text-foreground'} ${focusRing}`}>{productName}</a>
            {variant?.name && <div className="mt-1 text-xs text-muted-foreground">{variant.name}{dimension ? ` · ${dimension}` : ''}</div>}
            {tags.length > 0 && <div className="mt-1 text-xs text-muted-foreground">{tags.join(' · ')}</div>}
          </div>
          <ProductItemPrice product={product} unitPrice={unitPrice} originalUnitPrice={originalUnitPrice} variant={variant} isReward={isReward} />
        </div>
        {visibleCustomFields.length > 0 && (
          <dl className="mt-3 space-y-1">
            {visibleCustomFields.map((field: any, i: number) => (
              <div key={i} className="flex items-start gap-2 text-xs">
                <dt className="text-muted-foreground">{field.name}:</dt>
                <dd className="text-foreground">{renderCustomFieldValue(field, formatDateLong)}</dd>
              </div>
            ))}
          </dl>
        )}
        {productData?.isDigital && variant?.digitalFile && DIGITAL_DOWNLOAD_STATUSES.has(order?.status) && (
          <div className="mt-3 flex flex-col gap-2">
            {downloadUrl ? (
              <button type="button" disabled={downloading} onClick={async () => { setDownloading(true); await downloadDigitalFile(downloadUrl); setDownloading(false) }} className={`w-fit ${outlinedButton}`}>
                {downloading ? 'Татаж байна…' : 'Файл татах'}
              </button>
            ) : (
              <div className="text-xs text-muted-foreground">{isExpired ? 'Татах хугацаа дууссан байна' : 'Татах хязгаар хэтэрсэн байна'}</div>
            )}
            {downloadStatus && !isExpired && (
              <div className="text-[11px] text-muted-foreground tabular-nums">
                Үлдсэн: <span className="text-foreground">{downloadStatus.downloadsRemaining}/{downloadStatus.downloadsMax}</span>
                {downloadStatus.expiresAt && <span> · {formatDateNormal(new Date(downloadStatus.expiresAt))} хүртэл</span>}
              </div>
            )}
          </div>
        )}
      </div>
    </li>
  )
}

function ProductItemPrice({ product, unitPrice, originalUnitPrice, variant, isReward }: { product: any, unitPrice: number, originalUnitPrice: number | null, variant: any, isReward: boolean }) {
  const hasDiscountedPrice = originalUnitPrice !== null && unitPrice < originalUnitPrice
  const isFullyRefunded = product?.refunded && product?.quantity === 0
  const valueClass = 'text-sm ' + (isFullyRefunded ? 'text-muted-foreground line-through' : 'text-foreground')
  return (
    <div className="text-right shrink-0 tabular-nums">
      {isReward || hasDiscountedPrice ? (
        <>
          <div className={valueClass}>{unitPrice === 0 ? 'Үнэгүй' : formatPrice(unitPrice)}</div>
          <div className="text-xs text-muted-foreground line-through">{formatPrice(originalUnitPrice)}</div>
        </>
      ) : (
        <div className={valueClass}>{formatPrice(variant?.price || 0)}</div>
      )}
      <div className="mt-1 text-xs text-muted-foreground">× {(product?.quantity || 0) + (product?.refundedQuantity || 0)}</div>
    </div>
  )
}

function OrderItems({ order, vendor, pathname }: { order: any, vendor: any, pathname: string }) {
  return (
    <section>
      <h2 className={eyebrow}>Захиалсан бараа ({order?.products?.length || 0})</h2>
      <ul className="mt-2 divide-y divide-border border-y border-border">
        {order?.products?.map((product: any, i: number) => (
          <ProductItem key={i} product={product} vendor={vendor} pathname={pathname} order={order} />
        ))}
      </ul>
    </section>
  )
}

function OrderInfoCard({ order }: { order: any }) {
  const isPickup = order?.type === 'pickup'
  const orderProducts = order?.products || []
  const allDigital = orderProducts.length > 0 && orderProducts.every((p: any) => (p.product || p)?.isDigital === true)
  return (
    <section>
      <h2 className={eyebrow}>Захиалгын мэдээлэл</h2>
      <dl className="mt-2 divide-y divide-border border-y border-border">
        <InfoRow label="Хүлээн авагч">{getCustomerName(order) || '-'}</InfoRow>
        <InfoRow label="Утас">
          {order?.phone}
          {order?.additionalPhone ? <span className="text-muted-foreground"> · {order.additionalPhone}</span> : null}
        </InfoRow>
        {order?.email && <InfoRow label="Имэйл">{order.email}</InfoRow>}
        {!isPickup && !allDigital && (
          <InfoRow label="Хүргэх хаяг">
            <div>{formatOrderAddress(order?.address, order?.channel)}</div>
            {order?.address?.type && !FREEFORM_ADDRESS_CHANNELS.includes(order?.channel) && (
              <div className="mt-0.5 text-xs text-muted-foreground">{formatAddressType(order.address.type)}</div>
            )}
          </InfoRow>
        )}
        {isPickup && !allDigital && (order?.branchData || order?.branch) && (
          <InfoRow label="Авах салбар">
            {`${order?.branchData?.name || order?.branch?.name}, ${order?.branchData?.address || order?.branch?.address}`}
          </InfoRow>
        )}
        {(order?.orderCustomFields || []).filter(isCustomFieldVisible).map((field: any, i: number) => (
          <InfoRow key={i} label={field.name}>{renderCustomFieldValue(field, formatDateTimeLong)}</InfoRow>
        ))}
        <InfoRow label="Төлбөр">{PAYMENT_METHOD_LABELS[order?.paymentMethod] || PAYMENT_METHOD_LABELS.transfer}</InfoRow>
        <InfoRow label="Үүсгэсэн"><span className="tabular-nums">{formatDateTimeLong(order?.createdAt)}</span></InfoRow>
        {order?.paidAt && <InfoRow label="Төлсөн"><span className="tabular-nums">{formatDateTimeLong(order.paidAt)}</span></InfoRow>}
        {order?.readyAt && <InfoRow label="Бэлэн болсон"><span className="tabular-nums">{formatDateTimeLong(order.readyAt)}</span></InfoRow>}
        {order?.shippedAt && <InfoRow label="Хүргэлтэнд гарсан"><span className="tabular-nums">{formatDateTimeLong(order.shippedAt)}</span></InfoRow>}
        {order?.deliveredAt && <InfoRow label="Хүргэгдсэн"><span className="tabular-nums">{formatDateTimeLong(order.deliveredAt)}</span></InfoRow>}
        {order?.canceledAt && <InfoRow label="Цуцлагдсан"><span className="tabular-nums">{formatDateTimeLong(order.canceledAt)}</span></InfoRow>}
        {order?.refundedAt && <InfoRow label="Буцаагдсан"><span className="tabular-nums">{formatDateTimeLong(order.refundedAt)}</span></InfoRow>}
        {order?.deliveryFailedAt && <InfoRow label="Хүргэлт амжилтгүй"><span className="tabular-nums">{formatDateTimeLong(order.deliveryFailedAt)}</span></InfoRow>}
        {order?.deliveryFailedReason && <InfoRow label="Шалтгаан">{order.deliveryFailedReason}</InfoRow>}
      </dl>
    </section>
  )
}

function PriceBreakdown({ order, vendor }: { order: any, vendor: any }) {
  const { salePriceSavings, discountBreakdown } = calcPriceBreakdown(order)
  const totalDue = (order?.amount || 0) - (order?.totalRefunded || 0)
  const isDelivery = order?.type === 'delivery'
  const deposit = calcDepositBreakdown(order)

  return (
    <section>
      <h2 className={eyebrow}>Төлбөрийн задаргаа</h2>
      <dl className="mt-2 pt-2 border-t border-border text-[13px]">
        <PriceRow label="Барааны дүн" value={formatPrice(order?.subAmount)} />
        {salePriceSavings > 0 && <PriceRow label="Хямдрал" value={`-${formatPrice(salePriceSavings)}`} />}
        {discountBreakdown.map(d => <PriceRow key={d.id} label={`Урамшуулал (${d.name})`} value={`-${formatPrice(d.amount)}`} />)}
        {(order?.couponAmount || 0) > 0 && <PriceRow label={`Купон${order?.couponData?.name ? ` (${order.couponData.name})` : ''}`} value={`-${formatPrice(order?.couponAmount)}`} />}
        {(order?.giftCardAmount || 0) > 0 && <PriceRow label="Бэлгийн карт" value={`-${formatPrice(order?.giftCardAmount)}`} />}
        {(order?.loyaltyDiscountAmount || 0) > 0 && <PriceRow label="Лояалти хөнгөлөлт" value={`-${formatPrice(order?.loyaltyDiscountAmount)}`} />}
        {(order?.loyaltyVoucherAmount || 0) > 0 && <PriceRow label="Лояалти ваучер" value={`-${formatPrice(order?.loyaltyVoucherAmount)}`} />}
        {isDelivery && (
          <PriceRow label="Хүргэлт" value={vendor?.deliveryType === 'laterDelivery' && order?.deliveryPrice === 0 ? <span className="text-muted-foreground">Нэмэлтээр тооцно</span> : order?.deliveryPrice === 0 ? 'Үнэгүй' : formatPrice(order?.deliveryPrice)} />
        )}
        {(order?.totalRefunded || 0) > 0 && <PriceRow label="Буцаалт" value={`-${formatPrice(order?.totalRefunded)}`} tone="red" />}
      </dl>
      <div className="mt-2 pt-4 border-t border-border flex items-baseline justify-between gap-4">
        <span className="text-sm text-foreground">Нийт төлөх</span>
        <span className="text-2xl font-light tabular-nums text-foreground">{formatPrice(totalDue)}</span>
      </div>
      {deposit.isDeposit && (
        <div className="mt-4 pt-4 border-t border-border space-y-2 text-[13px]">
          <div className="flex items-baseline justify-between gap-4">
            <span className="text-muted-foreground">
              {DEPOSIT_LABELS.deposit}
              <span className="ml-2 text-[11px] tracking-[0.14em] uppercase">{deposit.isDepositPaid ? 'Төлсөн' : 'Хүлээгдэж буй'}</span>
            </span>
            <span className="tabular-nums text-foreground">{formatPrice(deposit.depositAmount)}</span>
          </div>
          <div className="flex items-baseline justify-between gap-4">
            <span className={deposit.outstanding > 0 ? 'text-foreground' : 'text-muted-foreground'}>
              {deposit.outstanding > 0 ? DEPOSIT_LABELS.balanceDue : DEPOSIT_LABELS.balance}
              {deposit.isFullyPaid && <span className="ml-2 text-[11px] tracking-[0.14em] uppercase text-muted-foreground">Төлсөн</span>}
            </span>
            <span className={`tabular-nums ${deposit.isFullyPaid || deposit.outstanding > 0 ? 'text-foreground' : 'text-muted-foreground'}`}>{formatPrice(deposit.balanceAmount) || '0₮'}</span>
          </div>
          {order?.depositTerms && (
            <p className="pt-3 mt-1 border-t border-border text-xs text-muted-foreground whitespace-pre-line">{order.depositTerms}</p>
          )}
        </div>
      )}
    </section>
  )
}

const PRICE_ROW_TONES = {
  default: { label: 'text-muted-foreground', value: 'text-foreground' },
  red: { label: 'text-red-600', value: 'text-red-600' },
} as const

function PriceRow({ label, value, tone = 'default' }: { label: string, value: any, tone?: keyof typeof PRICE_ROW_TONES }) {
  const t = PRICE_ROW_TONES[tone]
  return (
    <div className="flex justify-between gap-4 py-1.5">
      <dt className={t.label}>{label}</dt>
      <dd className={'tabular-nums ' + t.value}>{value}</dd>
    </div>
  )
}

function NoticeCard({ label, title, children }: { label: string, title: string, children: React.ReactNode }) {
  return (
    <section className="py-6 border-y border-border">
      <div className={eyebrow}>{label}</div>
      <div className="mt-3 text-lg tracking-tight text-foreground">{title}</div>
      <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">{children}</p>
    </section>
  )
}

function OnDeliveryCard({ order }: { order: any }) {
  return (
    <NoticeCard label="Төлбөр" title="Барааг авахдаа төлөх">
      Захиалгын дүн <span className="text-foreground tabular-nums">{formatPrice(order?.amount)}</span>-г барааг хүлээн авахдаа төлнө үү. Урьдчилж төлөх шаардлагагүй.
    </NoticeCard>
  )
}

function PendingPaymentBlock({ order, vendor, onRefresh }: { order: any, vendor: any, onRefresh: () => void, primary?: string }) {
  /*
    Split payment is checked before payAfterDelivery, and the order matters.

    A deposit order flips payAfterDelivery on the moment the deposit lands, so once the
    balance is outstanding the onDelivery branch below would swallow it and tell a
    customer who owes money to "pay at the door" — with no way to pay online at all.

    While the deposit itself is still due the order has payAfterDelivery = false, so it
    falls through to the ordinary rail blocks; they render getPayableAmount, which is the
    deposit at that point. Nothing special needed for that state.
  */
  const deposit = calcDepositBreakdown(order)
  if (deposit.isAwaitingBalance && !hasLiveInvoice(order)) {
    return <DepositBalanceCard order={order} vendor={vendor} onRefresh={onRefresh} />
  }
  if (order?.payAfterDelivery && !deposit.isDeposit) return <OnDeliveryCard order={order} />
  if (order?.paymentMethod === 'qpay') return <QpayBlock order={order} vendor={vendor} onRefresh={onRefresh} />
  if (order?.paymentMethod === 'storepay') return <StorepayBlock order={order} vendor={vendor} onRefresh={onRefresh} />
  if (order?.paymentMethod === 'pocket') return <PocketBlock order={order} vendor={vendor} onRefresh={onRefresh} />
  if (order?.paymentMethod === 'lend') return <LendBlock order={order} vendor={vendor} onRefresh={onRefresh} />
  if (order?.payAfterDelivery) return <OnDeliveryCard order={order} />
  return <BankTransferCard order={order} />
}

function CheckPaymentButton({ loading, success, onClick }: { loading: boolean, success: boolean, onClick: () => void, vendor?: any }) {
  return (
    <button type="button" onClick={onClick} disabled={loading} className={`w-full ${solidButton}`}>
      {loading ? 'Шалгаж байна…' : success ? 'Төлбөр төлөгдсөн байна' : 'Төлбөр шалгах'}
    </button>
  )
}

function PaymentSuccessCard() {
  return (
    <NoticeCard label="Төлбөр" title="Төлбөр амжилттай төлөгдлөө">
      Захиалга баталгаажиж, бид удахгүй боловсруулж эхэлнэ.
    </NoticeCard>
  )
}

function MethodCardHeader({ logo, title, subtitle, badge }: { logo: any, title: string, subtitle?: string, badge?: any }) {
  return (
    <div className="px-5 py-4 flex items-center gap-4 border-b border-border">
      <div className="size-10 bg-muted grid place-items-center shrink-0 p-2 overflow-hidden">{logo}</div>
      <div className="flex-1 min-w-0">
        <div className={eyebrow}>Төлбөрийн арга</div>
        <div className="mt-1 text-base text-foreground truncate">{title}{subtitle ? <span className="text-muted-foreground"> · {subtitle}</span> : null}</div>
      </div>
      {badge}
    </div>
  )
}

function QRFrame({ value, logoSrc, size = 200 }: { value: string, accent?: string, logoSrc?: any, size?: number }) {
  if (!value) return <div className="bg-muted animate-pulse" style={{ width: size + 24, height: size + 24 }} />
  return (
    <div className="relative border border-border bg-white p-3" style={{ width: size + 24, height: size + 24 }}>
      <QRCode value={value} size={size - 8} fgColor={QR_INK} bgColor="#ffffff" style={{ width: '100%', height: '100%' }} />
      {logoSrc && (
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 size-11 bg-white border border-border grid place-items-center p-1">
          <Image src={logoSrc} alt="" className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </div>
  )
}

function NumberedSteps({ steps }: { steps: string[], accent?: string, mutedNumbers?: boolean }) {
  return (
    <ol className="space-y-2.5 text-[13px] text-foreground">
      {steps.map((s, i) => (
        <li key={i} className="flex gap-3">
          <span className="shrink-0 w-5 text-xs tabular-nums text-muted-foreground pt-px">{String(i + 1).padStart(2, '0')}</span>
          <span>{s}</span>
        </li>
      ))}
    </ol>
  )
}

function BankChips({ banks }: { banks: any[] }) {
  const visible = banks.slice(0, 8)
  const hidden = banks.slice(8)
  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <div className="flex items-center -space-x-2">
        {visible.map((b: any, i: number) => (
          <div key={i} className="size-7 rounded-full border-2 border-background bg-white overflow-hidden shrink-0" title={b.name}>
            <img src={b.logo} alt={b.name || ''} className="w-full h-full object-cover" />
          </div>
        ))}
      </div>
      {hidden.length > 0 && <span className="text-xs text-muted-foreground tabular-nums">+{hidden.length}</span>}
    </div>
  )
}

function QpayBlock({ order, vendor, onRefresh }: { order: any, vendor: any, onRefresh: () => void }) {
  const { loading, success, check } = usePaymentChecker({ order, vendor, method: 'qpay', onRefresh })
  const { isInAppBrowser, appLinksBlocked, browserUrl, openInBrowser } = useInAppBrowser({ order })
  if (success) return <PaymentSuccessCard />
  if (!order) return null
  const banks = order?.qpay?.urls || []
  return (
    <section className="border border-border">
      <MethodCardHeader logo={<Image src={qpayLogo} alt="QPay" className="w-full h-full object-contain" />} title="QPay" subtitle="QR төлбөр" />
      <div className="grid sm:grid-cols-[auto_1fr] gap-6 p-5">
        <div className="flex flex-col items-center">
          <QRFrame value={order?.qpay?.QRCode} logoSrc={qpayLogo} size={200} />
          <div className="mt-2 text-[11px] text-muted-foreground">Захиалга <span className="font-mono text-foreground">#{order.customId}</span></div>
        </div>
        <div className="flex flex-col">
          <div className={eyebrow}>Төлөх дүн</div>
          <div className="mt-2 text-3xl font-light tabular-nums tracking-tight text-foreground">{formatPrice(getPayableAmount(order))}</div>
          <div className="mt-5">
            <NumberedSteps steps={['Банкны аппаа нээгээд QR уншуулах цэс рүү орно уу', 'Дээрх QR кодыг уншуулан төлбөрөө баталгаажуулна уу']} />
          </div>
          <div className="mt-6"><CheckPaymentButton loading={loading} success={success} onClick={check} /></div>
        </div>
      </div>
      {banks.length > 0 && (
        <>
          <div className="px-5 pb-5 sm:hidden">
            <div className={`mb-3 ${eyebrow}`}>Банкны апп шууд нээх</div>
            {isInAppBrowser && (
              <div className="mb-3 border border-border p-4">
                <p className="text-[13px] text-foreground">{IN_APP_BROWSER_LABELS.message}</p>
                <a href={browserUrl} onClick={openInBrowser} className={`mt-4 w-fit ${outlinedButton}`}>
                  {IN_APP_BROWSER_LABELS.action}<ExternalLink className="size-3.5" strokeWidth={1.5} />
                </a>
                <p className="mt-3 text-xs text-muted-foreground">{IN_APP_BROWSER_LABELS.hint}</p>
              </div>
            )}
            {!appLinksBlocked && (
              <div className="grid grid-cols-6 gap-1">
                {banks.map((b: any, i: number) => (
                  <a key={i} href={b.link} className={`aspect-square bg-white border border-border overflow-hidden transition-colors duration-150 hover:border-foreground ${focusRing}`}>
                    <img src={b.logo} alt={b.name || `Bank ${i + 1}`} className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            )}
          </div>
          <div className="hidden sm:flex px-5 py-4 items-center justify-between border-t border-border gap-3">
            <div className="text-xs text-muted-foreground shrink-0">Дэмжигдсэн банкууд</div>
            <BankChips banks={banks} />
          </div>
        </>
      )}
    </section>
  )
}

function PocketBlock({ order, vendor, onRefresh }: { order: any, vendor: any, onRefresh: () => void }) {
  const { loading, success, check } = usePaymentChecker({ order, vendor, method: 'pocket', onRefresh })
  if (success) return <PaymentSuccessCard />
  return (
    <section className="border border-border">
      <MethodCardHeader
        logo={<Image src={pocketzeroLogo} alt="PocketZero" className="w-full h-full object-contain" />}
        title="PocketZero"
        subtitle="Хүүгүй зээл"
        badge={<span className="hidden sm:inline text-[11px] tracking-[0.14em] uppercase text-muted-foreground">0% хүүтэй</span>}
      />
      <div className="grid sm:grid-cols-[auto_1fr] gap-6 p-5">
        <div className="flex flex-col items-center">
          <QRFrame value={order?.pocket?.qr} logoSrc={pocketzeroLogo} size={200} />
          <div className="mt-2 text-[11px] text-muted-foreground">Захиалга <span className="font-mono text-foreground">#{order?.customId}</span></div>
        </div>
        <div className="flex flex-col">
          <div className={eyebrow}>Зээлээр төлөх</div>
          <div className="mt-2 text-3xl font-light tabular-nums tracking-tight text-foreground">{formatPrice(getPayableAmount(order))}</div>
          <div className="mt-2 text-xs text-muted-foreground">Сард тэнцүү хуваан төлөх боломжтой. Нэмэлт хүү, шимтгэлгүй.</div>
          <div className="mt-5">
            <NumberedSteps steps={['Pocket аппыг нээж PocketZero цэс рүү орно уу', 'Дээрх QR кодыг уншуулж нөхцөлөө баталгаажуулна уу']} />
          </div>
          {order?.pocket?.deeplink && (
            <a href={order.pocket.deeplink} className={`mt-5 lg:hidden w-fit ${outlinedButton}`}>
              Pocket App нээх<ExternalLink className="size-3.5" strokeWidth={1.5} />
            </a>
          )}
          <div className="mt-6"><CheckPaymentButton loading={loading} success={success} onClick={check} /></div>
        </div>
      </div>
    </section>
  )
}

type PhoneMethodConfig = {
  brand: string
  tagline: string
  appName: string
  accent: string
  logo: any
  phone: string
  steps: string[]
}

function PhoneMethodCard({ order, vendor, method, config, onRefresh }: { order: any, vendor: any, method: PaymentEndpoint, config: PhoneMethodConfig, onRefresh: () => void }) {
  const { loading, success, check } = usePaymentChecker({ order, vendor, method, onRefresh })
  if (success) return <PaymentSuccessCard />
  return (
    <section className="border border-border">
      <MethodCardHeader logo={config.logo} title={config.brand} subtitle={config.tagline} />
      <div className="p-5 grid sm:grid-cols-2 gap-8">
        <div className="flex flex-col">
          <div className={eyebrow}>Зээлээр төлөх</div>
          <div className="mt-2 text-3xl font-light tabular-nums tracking-tight text-foreground">{formatPrice(getPayableAmount(order))}</div>
          <div className={`mt-6 mb-2 ${eyebrow}`}>Бүртгэлтэй утасны дугаар</div>
          <div className="min-h-11 flex items-center border border-border px-3 text-sm font-mono tabular-nums text-foreground">{config.phone || '—'}</div>
          <div className="mt-2 text-[11px] text-muted-foreground">{config.appName} апп дээр бүртгэлтэй утасны дугаар.</div>
          <div className="mt-6"><CheckPaymentButton loading={loading} success={success} onClick={check} /></div>
        </div>
        <div className="sm:border-l sm:border-border sm:pl-8">
          <div className={`mb-4 ${eyebrow}`}>Хэрхэн төлөх</div>
          <NumberedSteps steps={config.steps} />
          <p className="mt-5 text-[11px] text-muted-foreground">Мэдэгдэл таны утсанд хэдхэн секундийн дотор очно.</p>
        </div>
      </div>
    </section>
  )
}

function StorepayBlock({ order, vendor, onRefresh }: { order: any, vendor: any, onRefresh: () => void }) {
  return (
    <PhoneMethodCard
      order={order}
      vendor={vendor}
      method="storepay"
      onRefresh={onRefresh}
      config={{
        brand: 'StorePay',
        tagline: 'Хуваан төлөх үйлчилгээ',
        appName: 'StorePay',
        accent: '#0ea5e9',
        phone: order?.storepay?.phone,
        logo: <Image src={storepayLogo} alt="StorePay" className="w-full h-full object-contain" />,
        steps: [
          'Бүртгэлтэй утасны дугаар нь мөн эсэхийг шалгах',
          'StorePay апп дээрх ирсэн нэхэмжлэлийн Төлөх товчлуурыг дарна',
          'Хугацаагаа сонгож, зээлээ баталгаажуулна',
        ],
      }}
    />
  )
}

function LendBlock({ order, vendor, onRefresh }: { order: any, vendor: any, onRefresh: () => void }) {
  return (
    <PhoneMethodCard
      order={order}
      vendor={vendor}
      method="lend"
      onRefresh={onRefresh}
      config={{
        brand: 'LendMN',
        tagline: 'Бэлэн мөнгөн зээл',
        appName: 'LendMN',
        accent: '#f97316',
        phone: order?.lend?.phoneNumber,
        logo: <Image src={lendmnLogo} alt="LendMN" className="w-full h-full object-contain" />,
        steps: [
          'Бүртгэлтэй утасны дугаар нь мөн эсэхийг шалгах',
          'LendMN апп дээрх ирсэн нэхэмжлэлээ шалгана',
          'Хугацаагаа сонгож, зээлээ баталгаажуулна',
        ],
      }}
    />
  )
}

export default function OrderDetail({ order, vendor }: { order: any, vendor: any }) {
  const router = useRouter()
  const pathname = usePathname()
  const [countdown, setCountdown] = useState({ h: 0, m: 0, s: 0, expired: false })

  useEffect(() => {
    /*
      Split payment: a deposit order stays 'pending' for the whole 7-10 day lead time
      after its deposit lands. settleDeposit nulls expireAt, so the first clause already
      covers it — depositPaidAt is belt-and-braces against a stale cached order, because
      the failure mode is showing a live, paid order as cancelled.
    */
    if (!order?.expireAt || order?.status !== 'pending' || order?.depositPaidAt) return
    const target = new Date(order.expireAt).getTime()
    const tick = () => {
      const diff = Math.max(0, target - Date.now())
      setCountdown({
        h: Math.floor(diff / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
        expired: diff === 0,
      })
    }
    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [order?.expireAt, order?.status, order?.depositPaidAt])

  const isExpired = countdown.expired
  // A split-payment order whose deposit has landed sits in 'pending' for days by design;
  // it must never be rendered as cancelled by an expiry clock it is no longer subject to.
  const baseStatus = isExpired && order?.status === 'pending' && !order?.depositPaidAt ? 'canceled' : order?.status
  // Booklo-aware: advance to the Booklo delivery status when further along than order.status.
  const displayStatus = bookloDisplayStatus(order, baseStatus)
  const isRefundedStatus = order?.status === 'refunded' || order?.status === 'partiallyRefunded'
  // A pay-after-delivery order stays unpaid all the way through fulfilment, so the
  // "pay on handover" reminder has to survive past 'pending'.
  const isUnsettledOnDelivery = order?.payAfterDelivery && !order?.paidAt && order?.status !== 'canceled'
  // isExpired is not a reason to hide the block on a deposit-paid order — that clock
  // stopped applying the moment the deposit landed (see baseStatus above).
  const showPaymentBlock = (!isExpired || !!order?.depositPaidAt) && !isRefundedStatus && (order?.status === 'pending' || isUnsettledOnDelivery)
  const showPaidConfirmation = !isRefundedStatus && ['paid', 'ready', 'shipped', 'delivered'].includes(displayStatus)

  return (
    <>
      <div className="cont pt-2 pb-16 print:hidden">
        <OrderTitle order={order} status={displayStatus} />
        <div className="mt-10 lg:mt-12"><OrderStepper order={order} vendor={vendor} status={displayStatus} /></div>

        <div className="mt-10 lg:mt-12 space-y-8">
          {order?.expireAt && order?.status === 'pending' && !isExpired && !order?.depositPaidAt && (
            <Countdown countdown={countdown} />
          )}
          {showPaymentBlock && (
            <PendingPaymentBlock order={order} vendor={vendor} onRefresh={() => router.refresh()} />
          )}
          {showPaidConfirmation && (
            <PaidConfirmation order={order} status={displayStatus} />
          )}
          {(order?.status === 'refunded' || order?.status === 'partiallyRefunded') && (
            <RefundBanner order={order} />
          )}
        </div>

        <div className="mt-12 lg:mt-16 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,340px)] lg:gap-16">
          <div className="min-w-0 space-y-10">
            <OrderItems order={order} vendor={vendor} pathname={pathname} />
            {showPaidConfirmation && <DigitalDownloadRow order={order} vendor={vendor} />}
          </div>
          <aside className="mt-12 lg:mt-0 space-y-10 lg:sticky lg:top-24 lg:self-start">
            <PriceBreakdown order={order} vendor={vendor} />
            <OrderInfoCard order={order} />
          </aside>
        </div>

        <div className="mt-16"><FooterActions vendor={vendor} pathname={pathname} /></div>
      </div>
      <PrintableReceipt order={order} vendor={vendor} />
    </>
  )
}
